import React, { useState } from 'react';
import { X, ArrowLeft, ArrowRight, Check, Clock, Globe, MessageCircle, Shield, Phone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useRetry } from '../hooks/useRetry';
import LoadingSpinner from './LoadingSpinner';
import { validateEmail, validatePhoneNumber, validatePassword, sanitizeInput, formatPhoneNumber } from '../utils/validation';
import { trackUserRegistration } from '../utils/analytics';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface FormData {
  phoneNumber: string;
  countryCode: string;
  fullName: string;
  timezone: string;
  morningTime: string;
  eveningTime: string;
  weekendMessaging: boolean;
  responseMethod: 'text' | 'voice' | 'both';
  goalVisibility: 'private' | 'public';
  hasAccountabilityPartner: boolean;
  partnerEmail: string;
  partnerName: string;
  hasPermission: boolean;
  email: string;
  password: string;
}

const countryCodes = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
];

const timezones = [
  'America/New_York (EST)',
  'America/Chicago (CST)',
  'America/Denver (MST)',
  'America/Los_Angeles (PST)',
  'Europe/London (GMT)',
  'Europe/Paris (CET)',
  'Europe/Berlin (CET)',
  'Asia/Tokyo (JST)',
  'Asia/Shanghai (CST)',
  'Asia/Kolkata (IST)',
  'Australia/Sydney (AEDT)',
];

export default function RegistrationModal({ isOpen, onClose, onComplete }: RegistrationModalProps) {
  const { signUp } = useAuth();
  const { success, error: showError } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    phoneNumber: '',
    countryCode: '+1',
    fullName: '',
    timezone: 'America/New_York (EST)',
    morningTime: '08:00',
    eveningTime: '18:00',
    weekendMessaging: true,
    responseMethod: 'both',
    goalVisibility: 'private',
    hasAccountabilityPartner: false,
    partnerEmail: '',
    partnerName: '',
    hasPermission: false,
    email: '',
    password: '',
  });

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ 
      ...prev, 
      [field]: typeof value === 'string' ? sanitizeInput(value) : value 
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.email.trim()) {
          newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        
        const passwordValidation = validatePassword(formData.password);
        if (!passwordValidation.isValid) {
          newErrors.password = passwordValidation.errors[0];
        }
        
        if (!formData.phoneNumber.trim()) {
          newErrors.phoneNumber = 'Phone number is required';
        } else if (!validatePhoneNumber(formData.phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid phone number';
        }
        break;
      case 2:
        if (!formData.fullName.trim()) {
          newErrors.fullName = 'Full name is required';
        }
        if (!formData.timezone) {
          newErrors.timezone = 'Please select your timezone';
        }
        break;
      case 3:
        // Time validation is handled by input type="time"
        break;
      case 4:
        if (formData.hasAccountabilityPartner) {
          if (!formData.partnerEmail.trim()) {
            newErrors.partnerEmail = 'Partner email is required';
          } else if (!validateEmail(formData.partnerEmail)) {
            newErrors.partnerEmail = 'Please enter a valid email address';
          }
          if (!formData.partnerName.trim()) {
            newErrors.partnerName = 'Partner name is required';
          }
          if (!formData.hasPermission) {
            newErrors.hasPermission = 'Permission confirmation is required';
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const { error } = await signUp(formData.email, formData.password, {
        phoneNumber: `${formData.countryCode}${formData.phoneNumber.replace(/\D/g, '')}`,
        fullName: formData.fullName,
        timezone: formData.timezone,
        morningTime: formData.morningTime,
        eveningTime: formData.eveningTime,
        weekendMessaging: formData.weekendMessaging,
        responseMethod: formData.responseMethod,
        goalVisibility: formData.goalVisibility,
        hasAccountabilityPartner: formData.hasAccountabilityPartner,
        partnerEmail: formData.partnerEmail,
        partnerName: formData.partnerName,
        hasPermission: formData.hasPermission,
      });

      if (error) {
        // Handle specific Supabase error for existing user
        if (error.code === 'user_already_exists') {
          showError(
            'Account Already Exists', 
            'An account with this email already exists. Please sign in instead or use a different email address.'
          );
          onClose(); // Close the modal to guide user to login
        } else {
          showError('Registration Failed', error.message || 'Please try again');
        }
        return;
      }

      trackUserRegistration('email');
      setCurrentStep(5); // Success page
    } catch (error: any) {
      showError('Registration Failed', error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="registration-modal-title"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-4">
            {currentStep > 1 && currentStep < 5 && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <h2 id="registration-modal-title" className="text-xl font-semibold text-gray-900">
              {currentStep === 5 ? 'Welcome!' : 'Join OneWin.Today'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label="Close registration"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Progress Bar */}
        {currentStep < 5 && (
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Step {currentStep} of 4</span>
              <span className="text-sm text-gray-600">{Math.round((currentStep / 4) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-pink-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 4) * 100}%` }}
                role="progressbar"
                aria-valuenow={currentStep}
                aria-valuemin={1}
                aria-valuemax={4}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Step 1: Account Creation */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 text-pink-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Create your account
                </h3>
                <p className="text-gray-600">
                  We'll send you daily prompts via WhatsApp
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    aria-describedby={errors.email ? "email-error" : undefined}
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && (
                    <p id="email-error" className="text-red-500 text-sm mt-1" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => updateFormData('password', e.target.value)}
                    placeholder="Create a secure password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    aria-describedby={errors.password ? "password-error" : "password-help"}
                    aria-invalid={!!errors.password}
                  />
                  {errors.password ? (
                    <p id="password-error" className="text-red-500 text-sm mt-1" role="alert">
                      {errors.password}
                    </p>
                  ) : (
                    <p id="password-help" className="text-gray-500 text-sm mt-1">
                      Must be at least 8 characters with uppercase, lowercase, and number
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="flex space-x-2">
                    <select
                      value={formData.countryCode}
                      onChange={(e) => updateFormData('countryCode', e.target.value)}
                      className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                      aria-label="Country code"
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      id="phone"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => updateFormData('phoneNumber', formatPhoneNumber(e.target.value))}
                      placeholder="123-456-7890"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                      aria-describedby={errors.phoneNumber ? "phone-error" : undefined}
                      aria-invalid={!!errors.phoneNumber}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <p id="phone-error" className="text-red-500 text-sm mt-1" role="alert">
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Basic Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <Globe className="h-12 w-12 text-pink-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Tell us about yourself
                </h3>
                <p className="text-gray-600">
                  We'll personalize your experience
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => updateFormData('fullName', e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    aria-describedby={errors.fullName ? "fullName-error" : undefined}
                    aria-invalid={!!errors.fullName}
                  />
                  {errors.fullName && (
                    <p id="fullName-error" className="text-red-500 text-sm mt-1" role="alert">
                      {errors.fullName}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    id="timezone"
                    value={formData.timezone}
                    onChange={(e) => updateFormData('timezone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    aria-describedby={errors.timezone ? "timezone-error" : undefined}
                    aria-invalid={!!errors.timezone}
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                  {errors.timezone && (
                    <p id="timezone-error" className="text-red-500 text-sm mt-1" role="alert">
                      {errors.timezone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Messaging Preferences */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <Clock className="h-12 w-12 text-pink-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  When should we message you?
                </h3>
                <p className="text-gray-600">
                  Set your preferred times for daily check-ins
                </p>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="morningTime" className="block text-sm font-medium text-gray-700 mb-2">
                      Morning Prompt
                    </label>
                    <input
                      id="morningTime"
                      type="time"
                      value={formData.morningTime}
                      onChange={(e) => updateFormData('morningTime', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="eveningTime" className="block text-sm font-medium text-gray-700 mb-2">
                      Evening Check-in
                    </label>
                    <input
                      id="eveningTime"
                      type="time"
                      value={formData.eveningTime}
                      onChange={(e) => updateFormData('eveningTime', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.weekendMessaging}
                      onChange={(e) => updateFormData('weekendMessaging', e.target.checked)}
                      className="w-5 h-5 text-pink-400 border-gray-300 rounded focus:ring-pink-400"
                    />
                    <span className="text-gray-700">Send messages on weekends</span>
                  </label>
                </div>

                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-3">
                    Preferred Response Method
                  </legend>
                  <div className="space-y-2">
                    {[
                      { value: 'text', label: 'Text messages only' },
                      { value: 'voice', label: 'Voice messages only' },
                      { value: 'both', label: 'Both text and voice' },
                    ].map((option) => (
                      <label key={option.value} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="responseMethod"
                          value={option.value}
                          checked={formData.responseMethod === option.value}
                          onChange={(e) => updateFormData('responseMethod', e.target.value as 'text' | 'voice' | 'both')}
                          className="w-4 h-4 text-pink-400 border-gray-300 focus:ring-pink-400"
                        />
                        <span className="text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            </div>
          )}

          {/* Step 4: Privacy & Accountability */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center">
                <Shield className="h-12 w-12 text-pink-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Privacy & Accountability
                </h3>
                <p className="text-gray-600">
                  Choose your privacy settings and accountability options
                </p>
              </div>

              <div className="space-y-6">
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-3">
                    Goal Visibility
                  </legend>
                  <div className="space-y-2">
                    {[
                      { value: 'private', label: 'Private - Only you can see your goals' },
                      { value: 'public', label: 'Public - Share progress with community' },
                    ].map((option) => (
                      <label key={option.value} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="goalVisibility"
                          value={option.value}
                          checked={formData.goalVisibility === option.value}
                          onChange={(e) => updateFormData('goalVisibility', e.target.value as 'private' | 'public')}
                          className="w-4 h-4 text-pink-400 border-gray-300 focus:ring-pink-400"
                        />
                        <span className="text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="border-t border-gray-200 pt-6">
                  <label className="flex items-center space-x-3 mb-4">
                    <input
                      type="checkbox"
                      checked={formData.hasAccountabilityPartner}
                      onChange={(e) => updateFormData('hasAccountabilityPartner', e.target.checked)}
                      className="w-5 h-5 text-pink-400 border-gray-300 rounded focus:ring-pink-400"
                    />
                    <div>
                      <span className="text-gray-900 font-medium">Add an accountability partner</span>
                      <p className="text-sm text-gray-600">They'll receive updates on your progress</p>
                    </div>
                  </label>

                  {formData.hasAccountabilityPartner && (
                    <div className="space-y-4 ml-8">
                      <div>
                        <label htmlFor="partnerName" className="block text-sm font-medium text-gray-700 mb-2">
                          Partner's Name
                        </label>
                        <input
                          id="partnerName"
                          type="text"
                          value={formData.partnerName}
                          onChange={(e) => updateFormData('partnerName', e.target.value)}
                          placeholder="Partner's first name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                          aria-describedby={errors.partnerName ? "partnerName-error" : undefined}
                          aria-invalid={!!errors.partnerName}
                        />
                        {errors.partnerName && (
                          <p id="partnerName-error" className="text-red-500 text-sm mt-1" role="alert">
                            {errors.partnerName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="partnerEmail" className="block text-sm font-medium text-gray-700 mb-2">
                          Partner's Email
                        </label>
                        <input
                          id="partnerEmail"
                          type="email"
                          value={formData.partnerEmail}
                          onChange={(e) => updateFormData('partnerEmail', e.target.value)}
                          placeholder="Partner's email address"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                          aria-describedby={errors.partnerEmail ? "partnerEmail-error" : undefined}
                          aria-invalid={!!errors.partnerEmail}
                        />
                        {errors.partnerEmail && (
                          <p id="partnerEmail-error" className="text-red-500 text-sm mt-1" role="alert">
                            {errors.partnerEmail}
                          </p>
                        )}
                      </div>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={formData.hasPermission}
                          onChange={(e) => updateFormData('hasPermission', e.target.checked)}
                          className="w-4 h-4 text-pink-400 border-gray-300 rounded focus:ring-pink-400"
                          aria-describedby={errors.hasPermission ? "permission-error" : undefined}
                          aria-invalid={!!errors.hasPermission}
                        />
                        <span className="text-sm text-gray-700">I have their permission</span>
                      </label>
                      {errors.hasPermission && (
                        <p id="permission-error" className="text-red-500 text-sm" role="alert">
                          {errors.hasPermission}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {currentStep === 5 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <Check className="h-10 w-10 text-emerald-500" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Welcome to OneWin.Today!
                </h3>
                <p className="text-gray-600 mb-4">
                  You'll receive your first morning prompt at{' '}
                  <span className="font-medium">
                    {new Date(`2000-01-01T${formData.morningTime}`).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  Check your WhatsApp for a welcome message from us!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100">
          {currentStep < 5 ? (
            <button
              onClick={handleNext}
              disabled={isSubmitting}
              className="w-full bg-pink-500 text-white py-3 rounded-xl hover:bg-pink-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
            >
              {isSubmitting ? (
                <LoadingSpinner size="sm" color="gray" />
              ) : (
                <>
                  <span>
                    {currentStep === 1 ? 'Begin' : currentStep === 4 ? 'Complete Setup' : 'Continue'}
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleComplete}
              className="w-full bg-emerald-500 text-white py-3 rounded-xl hover:bg-emerald-600 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            >
              Go to Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}